import {
  Text,
  GreyGrey70,
  GreyGrey80,
  Primary13Regular,
  Primary14SemiBold,
  Box,
  Flex,
  Grid
} from '@sequencehq/core-components'
import { taxStatusOptions } from 'modules/Customers/data.ts'
import { CustomerAliases } from './CustomerAliases'
import { CustomerCustomProperties } from './CustomerCustomProperties'
import { CustomerLinkedServices } from './CustomerLinkedServices'
import { CustomerRelationship } from './CustomerRelationship'
import { useCustomerContext } from 'Customer/hooks/useCustomerContext'
import { formatAddress } from '@sequencehq/utils'
import { CustomerLogo } from './CustomerLogo'
import { toCountryLabel, toStateLabel } from '@sequencehq/api'
import { NO_ADDRESS_LABEL } from 'modules/Customers/data'
import {
  Address,
  getBillingAddress,
  getShippingAddress
} from '@sequencehq/api/utils/commonModels'
import { useFeatureFlags } from 'hooks/useFeatureFlags'

export const CustomerMeta = () => {
  const { customer, customerAliases, taxRegistrations } = useCustomerContext()
  const { useCustomerShippingAddresses } = useFeatureFlags()

  const customProperties = customer.customProperties ?? {}
  const billingAddress = getBillingAddress(customer.addresses)
  const shippingAddress = getShippingAddress(customer.addresses)
  const hasCustomProperties = Object.keys(customProperties).length > 0

  return (
    <Flex
      data-testid="customer-meta"
      p={5}
      flexDirection="column"
      gap="16px"
      width="100%"
    >
      <CustomerLogo customer={customer} />
      <Text {...Primary14SemiBold}>{customer.legalName}</Text>
      <Grid
        templateColumns={hasCustomProperties ? '1fr 1fr' : '1fr'}
        columnGap="16px"
        mt={4}
        alignItems="start"
      >
        <Grid
          templateColumns="150px 1fr"
          gap={4}
          height="fit-content"
          color={GreyGrey80}
        >
          <CustomerRelationship customer={customer} />
          <CustomerAliases aliases={customerAliases} />

          {customer.label && (
            <>
              <Text {...Primary13Regular} color={GreyGrey70}>
                Label
              </Text>
              <Text
                data-testid="customer-meta.label"
                {...Primary13Regular}
                color={GreyGrey80}
              >
                {customer.label}
              </Text>
            </>
          )}

          <Text {...Primary13Regular} color={GreyGrey70}>
            Tax status
          </Text>
          <Text {...Primary13Regular} color={GreyGrey80}>
            {
              taxStatusOptions[
                customer?.taxStatus as keyof typeof taxStatusOptions
              ]
            }
          </Text>
          {taxRegistrations.map((reg, idx) => (
            <>
              <Text
                key={`loc-${reg.id}`}
                {...Primary13Regular}
                color={GreyGrey70}
              >
                {taxRegistrations.length > 1
                  ? `Tax location ${idx + 1}`
                  : 'Tax location'}
              </Text>
              <Text {...Primary13Regular} color={GreyGrey80}>
                {reg.state && `${toStateLabel(reg.state)}, `}
                {toCountryLabel(reg.country)}
              </Text>
              <Text
                key={`id-${reg.id}`}
                {...Primary13Regular}
                color={GreyGrey70}
              >
                {taxRegistrations.length > 1
                  ? `Tax registration ID ${idx + 1}`
                  : 'Tax registration ID'}
              </Text>
              <Text {...Primary13Regular} color={GreyGrey80}>
                {reg.taxIdentifier}
              </Text>
            </>
          ))}

          <Text {...Primary13Regular} color={GreyGrey70}>
            {useCustomerShippingAddresses ? 'Billing address' : 'Address'}
          </Text>
          <AddressValue
            data-testid="customer-meta.address"
            address={billingAddress}
          />
          {useCustomerShippingAddresses && shippingAddress && (
            <>
              <Text {...Primary13Regular} color={GreyGrey70}>
                Shipping address
              </Text>
              <AddressValue
                data-testid="customer-meta.shippingAddress"
                address={shippingAddress}
              />
            </>
          )}
          <CustomerLinkedServices integrationIds={customer.integrationIds} />
        </Grid>
        {hasCustomProperties && (
          <CustomerCustomProperties customProperties={customProperties} />
        )}
      </Grid>
    </Flex>
  )
}

const AddressValue = ({
  address,
  'data-testid': testId
}: {
  address: Address | null
  'data-testid': string
}) => (
  <Box data-testid={testId}>
    {address ? (
      formatAddress(address).map((textValue, index) => (
        <Text {...Primary13Regular} maxW="120px" color={GreyGrey80} key={index}>
          {textValue}
        </Text>
      ))
    ) : (
      <Text {...Primary13Regular} color={GreyGrey70}>
        {NO_ADDRESS_LABEL}
      </Text>
    )}
  </Box>
)
